import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { errorThrown } from '#/shared/slices/error';
import RouteBadge from '#/shared/components/RouteBadge';
import AlertsBadge from '#/transit-tracker/components/AlertsBadge';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import { useGetAlertsQuery } from '#/services/developerApi';
import { ALERTS_POLLING_INTERVAL } from '#/transit-tracker/constants';
import * as Styled from './styled';

function RouteList({
  routes,
  defaultMessage,
  navigateToRoute,
  navigateToRouteAlerts,
}) {
  const { data, isError, isLoading } = useGetAlertsQuery(undefined, {
    pollingInterval: ALERTS_POLLING_INTERVAL,
  });
  const dispatch = useDispatch();

  if (isLoading) {
    return null;
  }
  if (isError) {
    // TODO: Currently tied into the existing error boundary
    //  Develop error handling notification that doesn't impact the entire application
    dispatch(errorThrown());
    return null;
  }

  return (
    <Styled.RouteListBody>
      {routes.length ? (
        routes.map(r => (
          <Styled.RouteRow key={r.id}>
            <Styled.RouteDetailsColumn onClick={() => navigateToRoute(r.id)}>
              <Styled.RouteBadgeColumn aria-hidden="true">
                <RouteBadge routeId={r.id} />
              </Styled.RouteBadgeColumn>
              <Styled.RouteNameColumn>{r.vanityName}</Styled.RouteNameColumn>
              <ScreenReaderText>
                <FormattedMessage id="routes.routedetails_acc" />
              </ScreenReaderText>
            </Styled.RouteDetailsColumn>
            {data.alertCountsByRoute[r.id] && (
              <Styled.RouteAlertsColumn
                onClick={() => navigateToRouteAlerts(r.id)}
              >
                <div aria-hidden="true">
                  <AlertsBadge
                    text={data.alertCountsByRoute[r.id].toString()}
                  />
                </div>
                <ScreenReaderText>
                  <FormattedMessage
                    id="routes.routealertscount_acc"
                    values={{ count: data.alertCountsByRoute[r.id] }}
                  />
                </ScreenReaderText>
              </Styled.RouteAlertsColumn>
            )}
          </Styled.RouteRow>
        ))
      ) : (
        <Styled.RouteRow>
          <Styled.NoRoutesWrapper>
            {defaultMessage || <FormattedMessage id="routes.noroutes" />}
          </Styled.NoRoutesWrapper>
        </Styled.RouteRow>
      )}
    </Styled.RouteListBody>
  );
}

RouteList.propTypes = {
  /** Collection of objects that describe transit routes */
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      vanityName: PropTypes.string.isRequired,
      routeColor: PropTypes.string,
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  /*
    We bypass the linter here because we are seeing unusual behavior with
    validation here. The object uses route ids as keys and for some reason this
    is making react validate this as object or array - the opposite of what we
    choose
  */
  /** Displayed when the route collection is empty */
  defaultMessage: PropTypes.string,
  /** Navigates the user to the route details for this route */
  navigateToRoute: PropTypes.func.isRequired,
  /** Navigates to a listing of alerts for the provided route */
  navigateToRouteAlerts: PropTypes.func.isRequired,
};

RouteList.defaultProps = {
  defaultMessage: null,
};

export default RouteList;
